import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getToken, setToken } from "@/lib/api";

const LiveCtx = createContext(null);
export const useLive = () => useContext(LiveCtx);

const WS_URL = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws`;

export function LiveProvider({ children }) {
  const wsRef = useRef(null);
  const snaps = useRef({});        // instrument_token -> snapshot
  const refs = useRef(new Map());  // id string -> refcount
  const dirty = useRef(false);
  const [version, setVersion] = useState(0);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let stop = false;
    let ws;
    let retry;
    const connect = () => {
      ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(getToken() || "")}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        const ids = [...refs.current.keys()];
        if (ids.length) ws.send(JSON.stringify({ action: "subscribe", ids }));
      };
      ws.onmessage = (e) => {
        const m = JSON.parse(e.data);
        if (m.type === "mode") {
          snaps.current = {};
          dirty.current = true;
          window.dispatchEvent(new Event("mdb-mode"));
        } else if (m.type === "snapshot" && m.data) {
          snaps.current[m.data.instrument_token] = m.data;
          dirty.current = true;
        }
      };
      ws.onclose = (ev) => {
        setConnected(false);
        if (ev && ev.code === 4401) {
          stop = true;
          setToken(null);
          window.dispatchEvent(new Event("mdb-logout"));
          return;
        }
        if (!stop) retry = setTimeout(connect, 2000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    const flush = setInterval(() => {
      if (dirty.current) { dirty.current = false; setVersion((v) => v + 1); }
    }, 150);
    return () => { stop = true; clearTimeout(retry); clearInterval(flush); try { ws && ws.close(); } catch { /* noop */ } };
  }, []);

  const send = (action, ids) => {
    const ws = wsRef.current;
    if (ids.length && ws && ws.readyState === 1) ws.send(JSON.stringify({ action, ids }));
  };
  const subscribe = useCallback((ids) => {
    const add = [];
    ids.forEach((id) => {
      const c = refs.current.get(id) || 0;
      refs.current.set(id, c + 1);
      if (c === 0) add.push(id);
    });
    send("subscribe", add);
  }, []);
  const unsubscribe = useCallback((ids) => {
    const rm = [];
    ids.forEach((id) => {
      const c = refs.current.get(id) || 0;
      if (c <= 1) { refs.current.delete(id); rm.push(id); } else refs.current.set(id, c - 1);
    });
    send("unsubscribe", rm);
  }, []);

  const value = { connected, version, get: (tok) => snaps.current[tok], subscribe, unsubscribe };
  return <LiveCtx.Provider value={value}>{children}</LiveCtx.Provider>;
}

// Subscribe to the tokens of `rows` and return rows merged with the latest live snapshot.
export function useLiveRows(rows) {
  const live = useLive();
  const ids = (rows || []).filter((r) => r?.instrument_token).map((r) => String(r.instrument_token));
  const key = ids.join(",");
  useEffect(() => {
    if (!live || !ids.length) return undefined;
    live.subscribe(ids);
    return () => live.unsubscribe(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const v = live?.version;
  return (rows || []).map((r) => ({ ...r, ...(live?.get(r.instrument_token) || {}) }), v);
}

// Subscribe to a single token; returns the live snapshot (or null) merged by the caller.
export function useLiveOne(token) {
  const live = useLive();
  useEffect(() => {
    if (!live || !token) return undefined;
    const id = String(token);
    live.subscribe([id]);
    return () => live.unsubscribe([id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  return live?.get(token) || null;
}
